import { FOUNDER_CALL_LABEL, FOUNDER_CALL_URL } from '../founderCall';
import type { FeatureContent } from './types';

export const aiCandidateMatching: FeatureContent = {
  slug: 'ai-candidate-matching',
  path: '/features/ai-candidate-matching',
  scorecardConnector:
    'The match layer of the candidate scorecard: a defensible fit score, plus the interview playbook of gaps to probe.',
  audience: 'company',
  icon: 'Brain',
  name: 'Match Intelligence',
  stage: 2,
  navLabel: 'AI Candidate Matching',
  navDescription: 'Rank every applicant by real fit, with a score you can defend.',
  primaryKeyword: 'AI candidate matching',
  seo: {
    title: 'AI Candidate Matching Software | Talenture.ai',
    description:
      'AI candidate matching that ranks applicants by real fit across eight signals, not keywords. Every score is transparent and defensible, built on 4,600+ skills.',
    keywords: [
      'AI candidate matching',
      'AI candidate screening',
      'AI job matching',
      'skills-based matching',
      'semantic candidate matching',
      'candidate ranking software',
      'recruitment matching engine',
    ],
    ogTitle: 'AI Candidate Matching That Explains Itself | Talenture.ai',
    ogDescription:
      'Rank applicants by genuine fit across eight scored signals: direct and transferable skills, semantic similarity, experience, seniority, location, values, and salary. Every score is transparent and defensible.',
  },
  hero: {
    eyebrow: 'Stage 2 of the hiring mission · Find and understand candidates',
    whereThisFits:
      'Talenture ranks the applicants your role attracted, then hands the strongest to the preparation step.',
    h1: "Two hundred applicants share the job's keywords. Three can do the job.",
    subhead:
      'Talenture scores every applicant across 8 signals and shows you exactly why, so when the hiring manager asks "why this candidate?", the answer is on one page.',
    worksWithAts: true,
    primaryCta: { label: 'See a sample scorecard', href: '/sample-scorecard' },
    secondaryCta: { label: FOUNDER_CALL_LABEL, href: FOUNDER_CALL_URL },
  },
  definition: {
    heading: 'What is AI candidate matching?',
    body: 'AI candidate matching scores how well each applicant fits a role using direct and transferable skills, semantic similarity, experience, seniority, location, company values, and salary, instead of keyword overlap. Talenture weighs these eight signals into one score, ranks your pipeline automatically, and shows the evidence behind every result.',
  },
  sections: [
    {
      icon: 'Sliders',
      title: 'Eight signals, weighted for the role',
      body: 'Most tools match strings. Talenture scores each applicant across eight signals at once and weights them for the role, so a strong candidate is never lost to a missing buzzword.',
      bullets: [
        'Direct skills: the ones your role actually names.',
        'Transferable skills: a React developer can be a strong Vue hire.',
        'Semantic fit: the same strength, described in different words.',
        'Experience: how much of it is the work you are hiring for.',
        'Seniority and trajectory: career direction, not just years served.',
        'Location and work type: commute scored, relocation kept in the running.',
        'Company values: culture as a scored signal, not a gut call.',
        'Salary: expectations checked against your range, up front.',
      ],
    },
    {
      icon: 'Filter',
      title: 'Pre-screen rules and salary, made visible',
      body: 'Hard requirements on seniority, location, and salary are applied up front, and the result is never hidden. When a candidate sits just outside a rule but is worth a look, they are shown for hiring-manager flexibility with the reason attached.',
      bullets: [
        'Must-have rules on seniority, location, and salary',
        'Salary expectations checked against the role\'s range',
        'Borderline candidates shown for flexibility, with the reason',
      ],
    },
    {
      icon: 'Eye',
      title: 'Every ranking is defensible',
      body: 'When a hiring manager asks why a candidate ranked where they did, you have the answer per candidate: which requirements they cover, which skills matched and which are missing, the reasons behind the score, and the gaps to probe in an interview.',
      bullets: [
        'Requirements coverage, matched and missing skills',
        'Plain-language reasons behind every score',
        'Gaps to probe, ready for the interview',
      ],
    },
  ],
  visual: {
    component: 'ApplicationsBoardScreen',
    url: 'app.talenture.ai/roles/platform-engineer/applications',
    caption: 'Every applicant ranked, with the eight signals behind the score on the candidate.',
  },
  supportingVisuals: [
    {
      component: 'MatchScorecardScreen',
      url: 'app.talenture.ai/candidates/mira-n/match',
      heading: 'Open one candidate',
      caption:
        'The scorecard a recruiter reads: the verdict, the score breakdown, and a rail to the evidence, the next steps and the interview plan. Click through it.',
    },
    {
      component: 'CandidateDeepDiveScreen',
      url: 'app.talenture.ai/candidates/mira-n/match/deep-dive',
      heading: 'Then check the working',
      caption:
        'Every signal scored separately, with the evidence that produced it, so the number survives a hiring manager asking why.',
    },
  ],
  underTheHood: {
    intro:
      'Nothing below is something you have to operate. It is here because a score you cannot interrogate is a score you cannot defend, so this is how the number is actually produced.',
    sections: [
      {
        icon: 'Network',
        title: 'The skills graph behind transferable skills',
        body: 'A React developer can be a strong Vue hire. The graph gives partial credit for substitutes, overlaps, and prerequisites, so you see candidates a literal filter would bury.',
        bullets: [
          '4,600+ verified skills and 21,700+ AI-validated relationships across 240+ tech roles',
          'Built on ESCO v1 (EU) and O*NET (US Department of Labor)',
          'Containment matching: "Spring Boot" satisfies "Spring"',
        ],
      },
      {
        icon: 'ScanSearch',
        title: 'Semantic matching, not string matching',
        body: 'Two profiles can describe the same strength in different words. Talenture compares the meaning of a profile against the role using vector embeddings, so a candidate from an adjacent field still surfaces when the fit is real.',
        bullets: [
          'Vector embeddings compare meaning, not exact wording',
          'Cosine similarity scores how close a profile is to the role',
          'A similarity gate blocks weak pairings inside a shared domain',
        ],
      },
      {
        icon: 'TrendingUp',
        title: 'How seniority and trajectory are scored',
        body: 'The engine reads career direction, not just years. It rewards genuine growth, recognises a logical next step, and flags overqualification, so a senior architect is not ranked first for a junior role and a fast riser is not penalised for a short tenure.',
        bullets: [
          'Progression bonus for a logical next step up',
          'Overqualification flagged, not silently ranked first',
          'Title history read for real career direction',
        ],
      },
      {
        icon: 'Compass',
        title: 'How location is scored',
        body: 'Commute distance is calculated from real coordinates for onsite roles, eased for hybrid and remote, and candidates open to relocation get a fair floor instead of a hard cut.',
        bullets: [
          'Commute distance scored from real coordinates',
          'Onsite, hybrid, and remote weighted differently',
          'Candidates open to relocation kept in the running',
        ],
      },
    ],
  },
  howTo: {
    heading: 'How AI candidate matching works',
    intro:
      'Four analytical layers run in parallel for every applicant, each producing a partial score that feeds the overall match.',
    steps: [
      {
        title: 'Skills graph: credit for transferable and related skills',
        body: 'A graph of 4,600+ verified skills and 21,700+ AI-validated relationships identifies substitutes, overlaps, and prerequisites so candidates with transferable experience surface instead of being buried by literal filters.',
      },
      {
        title: 'Semantic matching: meaning over exact wording',
        body: 'Vector embeddings compare the meaning of each candidate profile against the role using cosine similarity, so the same strength described in different words still registers. A similarity gate blocks weak pairings inside a shared domain.',
      },
      {
        title: 'Seniority and trajectory: career direction, not just years',
        body: 'The engine reads career progression, not only tenure. A logical next-step is rewarded, overqualification is flagged rather than silently ranked first, and a fast riser is not penalised for a short title history.',
      },
      {
        title: 'Location scoring: real commute distance, not a radius filter',
        body: 'Commute distance is calculated from real coordinates for onsite roles, eased for hybrid and remote, and candidates open to relocation receive a fair floor score instead of a hard exclusion.',
      },
    ],
  },
  proofPoints: [
    { value: 'Every', label: 'applicant scored, not just the first twenty' },
    { value: '8', label: 'signals behind every score' },
    { value: 'One page', label: 'of evidence per candidate' },
  ],
  faq: [
    {
      question: 'How is this different from keyword matching in an ATS?',
      answer:
        'An ATS filter looks for exact words and discards anyone who phrased things differently. Talenture scores the underlying skills and their relationships, so a candidate with transferable experience still surfaces. You rank by fit, not by who happened to use the right keyword.',
    },
    {
      question: 'What signals does the match score include?',
      answer:
        'Eight: direct skills, transferable related skills, semantic similarity, experience, seniority and trajectory, location and work type, company values, and salary fit. Each is weighted for the role and combined into one score you can break down per candidate.',
    },
    {
      question: 'How does semantic matching work?',
      answer:
        'Talenture turns each profile and role into a vector embedding and compares them with cosine similarity, so it recognises when two profiles describe the same strength in different words. A similarity gate blocks weak pairings, for example two unrelated skills that happen to share a broad domain.',
    },
    {
      question: 'Does it account for location and salary?',
      answer:
        'Yes. Commute distance is scored for onsite roles and eased for hybrid and remote, with a fair floor for candidates open to relocation. Salary expectations are checked against the role\'s range, and a candidate just outside a rule can still be shown for hiring-manager flexibility.',
    },
    {
      question: 'Can I see why a candidate received their score?',
      answer:
        'Yes. Every match breaks down into requirements coverage, matched and missing skills, experience, location, values, and salary signals, with the specific evidence behind each one. The reasoning is in plain language, so you can justify any ranking to a hiring manager.',
    },
    {
      question: 'What data is the matching built on?',
      answer:
        'A skills graph of 4,600+ verified skills and 21,700+ AI-validated relationships across 240+ tech roles, built on ESCO v1 (EU) and O*NET (US Department of Labor). It understands progression, transferability, and semantic similarity between skills.',
    },
    {
      question: 'Does it work for non-technical roles?',
      answer:
        'The graph is strongest for Data, AI, and Engineering roles, which is where Talenture focuses. It still scores adjacent and operational roles, and coverage keeps expanding as more roles are mapped.',
    },
    {
      question: 'Will the matching introduce bias?',
      answer:
        'Scoring is based on skills, experience, and values, not demographic signals. Because every score is explainable, you can audit exactly why any candidate ranked where they did, which is far harder with a black-box tool.',
    },
  ],
  relatedSlugs: ['ai-candidate-profiles', 'candidate-feedback', 'match-insights'],
};
